import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Alert, Card, Col, Row, Segmented, Spin } from "antd";
import { observer } from "mobx-react-lite";

import { getReleases } from "../api/releases";
import { userStore } from "../stores/userStore";
import { getVisibleApiKeyServices } from "../utils/apiKeyServices";
import { extendProductsWithReleases, OS_LIST } from "../utils/softwareUtils";
import { usePageTitle } from "../utils/usePageTitle";
import { ExternalServicesPage } from "./ExternalServicesPage";

import "./SoftwarePage.css";

import type { Release } from "../api/releases";

const SoftwareTab = observer(function SoftwareTab() {
  const { t, i18n } = useTranslation();
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getReleases()
      .then((data) => {
        if (!cancelled) {
          setReleases(data);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isRuExperience = i18n.language.startsWith("ru") && userStore.isRuRegion;
  const isRuLangNonRuRegion =
    i18n.language.startsWith("ru") && !userStore.isRuRegion;
  const products = extendProductsWithReleases(releases, isRuExperience);
  const pricingUrl = userStore.isRuRegion
    ? "https://nextgis.ru/pricing-base/#soft"
    : "https://nextgis.com/pricing-base/#soft";

  return (
    <>
      <p className="software-page__description">
        <Trans
          i18nKey={
            isRuLangNonRuRegion
              ? "software.descriptionNonRu"
              : "software.description"
          }
          components={{
            lnk: <a href={pricingUrl} target="_blank" rel="noreferrer" />,
            accountIcon: (
              <img
                className="vcenter"
                src={`${import.meta.env.BASE_URL}img/nextgis_account_icon.png`}
                alt="NextGIS Account Toolbar"
              />
            ),
          }}
        />
      </p>

      {loading && (
        <div className="software-page__loading">
          <Spin size="large" />
        </div>
      )}

      {!loading && error && (
        <Alert
          type="error"
          showIcon
          description={t("software.errorLoad")}
          className="software-page__error"
        />
      )}

      {!loading && !error && products.length > 0 && (
        <Row gutter={[24, 24]} className="software-page__products">
          {products.map((product) => (
            <Col key={product.id} xs={12} sm={8} md={6} lg={6}>
              <div className="software-page__product-logo">
                <img
                  src={product.iconUrl}
                  alt={product.name}
                  className="software-page__product-logo-pic"
                />
              </div>
              <div className="software-page__product-name">{product.name}</div>
              {product.downloadPage && (
                <div>
                  <a
                    href={product.downloadPage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="software-page__download-link"
                  >
                    {t("software.download.qgis")}
                  </a>
                </div>
              )}
              {OS_LIST.map((os) => (
                <div key={os}>
                  {product.installers[`${os}-network`] && (
                    <div>
                      <a
                        href={product.installers[`${os}-network`].link}
                        target={
                          product.installers[`${os}-network`].external
                            ? "_blank"
                            : "_self"
                        }
                        rel="noopener noreferrer"
                        className="software-page__download-link"
                      >
                        {t(`software.download.${os}-network`)}
                      </a>
                    </div>
                  )}
                  {product.installers[`${os}-standalone`] && (
                    <div>
                      <a
                        href={product.installers[`${os}-standalone`].link}
                        target={
                          product.installers[`${os}-standalone`].external
                            ? "_blank"
                            : "_self"
                        }
                        rel="noopener noreferrer"
                        className="software-page__download-link"
                      >
                        {t(`software.download.${os}-standalone`)}
                      </a>
                    </div>
                  )}
                </div>
              ))}
            </Col>
          ))}
        </Row>
      )}

      <Card className="software-page__plugins">
        <h3>
          {t(
            isRuExperience
              ? "software.pluginTitleNgQgis"
              : "software.pluginTitle",
          )}
        </h3>
        <p
          dangerouslySetInnerHTML={{
            __html: isRuLangNonRuRegion
              ? t("software.pluginDescriptionNonRu")
              : t("software.pluginDescription", {
                  rosreestrPrefix: isRuExperience ? "Rosreestr Tools, " : "",
                }),
          }}
        />
      </Card>
    </>
  );
});

export const SoftwarePage = observer(function SoftwarePage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const apiKeyServices = getVisibleApiKeyServices(
    i18n.language,
    userStore.isRuRegion,
  );
  const hasApiKeyServices = apiKeyServices.length > 0;
  const title = t(
    hasApiKeyServices ? "software.combinedTitle" : "software.title",
  );
  const activeTab =
    hasApiKeyServices && searchParams.get("tab") === "api-keys"
      ? "api-keys"
      : "software";

  usePageTitle(title);

  const changeTab = (tab: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tab === "api-keys") nextParams.set("tab", tab);
    else nextParams.delete("tab");
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="page software-page">
      <h1>{title}</h1>
      {hasApiKeyServices ? (
        <>
          <Segmented
            size="large"
            value={activeTab}
            onChange={changeTab}
            options={[
              {
                value: "software",
                label: t("software.tabs.software"),
              },
              {
                value: "api-keys",
                label: t("software.tabs.apiKeys"),
              },
            ]}
            className="software-page__nav"
          />
          <div className="software-page__content">
            <div
              style={{ display: activeTab === "software" ? undefined : "none" }}
            >
              <SoftwareTab />
            </div>
            <div
              style={{
                display: activeTab === "api-keys" ? undefined : "none",
              }}
            >
              <ExternalServicesPage
                services={apiKeyServices}
                isActive={activeTab === "api-keys"}
              />
            </div>
          </div>
        </>
      ) : (
        <SoftwareTab />
      )}
    </div>
  );
});
